/**
 * sirus/SettingsPanel — R232q20 Phase 4.
 *
 * 600×500 modal Settings panel. Opened via the «Меню» micro-button or by
 * dispatching `aiwari-open-settings`. Renders a six-tab layout matching the
 * WoW 3.3.5a Interface Options shape: Игра / Интерфейс / Боевая система /
 * Звук / Видео / Управление.
 *
 * Only the «Интерфейс» tab is fully wired in Phase 4 — it reads/writes the
 * existing `useUiSettings` slice (action-bar opt-ins, scalars). Other tabs
 * render a placeholder «Настройки скоро появятся» card and a list of the
 * controls planned for that tab. They are intentionally non-functional so
 * the user sees the full structural plan without half-built knobs.
 *
 * z-index: 50 (above all other panels but under modal-dialog level 60).
 */

import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { useUiSettings } from '../../state/ui-settings-store.js';

/* ─────────────────────────────────────────────────────────────────
 * Tab definitions
 * ──────────────────────────────────────────────────────────────── */

type TabId = 'game' | 'interface' | 'combat' | 'sound' | 'video' | 'controls';

const TABS: { id: TabId; label: string }[] = [
  { id: 'game', label: 'Игра' },
  { id: 'interface', label: 'Интерфейс' },
  { id: 'combat', label: 'Боевая система' },
  { id: 'sound', label: 'Звук' },
  { id: 'video', label: 'Видео' },
  { id: 'controls', label: 'Управление' },
];

/* ─────────────────────────────────────────────────────────────────
 * Component
 * ──────────────────────────────────────────────────────────────── */

interface SettingsPanelProps {
  /** Optional controlled-mode props. When omitted, panel opens on
   *  `aiwari-open-settings` and closes on Esc / overlay click. */
  open?: boolean;
  onClose?: () => void;
}

export function SettingsPanel({ open: openProp, onClose }: SettingsPanelProps = {}) {
  const isControlled = typeof openProp === 'boolean';
  const [openInner, setOpenInner] = useState(false);
  const open = isControlled ? !!openProp : openInner;
  const close = () => {
    if (isControlled) onClose?.();
    else setOpenInner(false);
  };

  const [tab, setTab] = useState<TabId>('interface');

  /* ── Listen for global open event (uncontrolled mode) ── */
  useEffect(() => {
    if (isControlled) return;
    const handler = () => setOpenInner(true);
    window.addEventListener('aiwari-open-settings', handler);
    return () => window.removeEventListener('aiwari-open-settings', handler);
  }, [isControlled]);

  /* ── Esc closes ── */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal
      style={overlayStyle}
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={panelStyle}
      >
        {/* Header */}
        <div style={headerStyle}>
          <span style={titleStyle}>Настройки</span>
          <button type="button" onClick={close} style={closeBtnStyle} aria-label="Закрыть">×</button>
        </div>

        {/* Body: tab rail + content */}
        <div style={bodyStyle}>
          <div style={tabRailStyle}>
            {TABS.map((t) => {
              const active = t.id === tab;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  style={{
                    ...tabBtnStyle,
                    background: active ? 'rgba(201,169,106,0.15)' : 'transparent',
                    color: active ? 'var(--aiwari-gold-soft, #FFD479)' : 'var(--aiwari-fg-muted, #B19B7E)',
                    borderLeftColor: active ? 'var(--aiwari-gold, #FAB300)' : 'transparent',
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <div style={contentStyle}>
            {tab === 'interface' && <InterfaceTab />}
            {tab === 'game' && <PlaceholderTab title="Игра" />}
            {tab === 'combat' && <PlaceholderTab title="Боевая система" />}
            {tab === 'sound' && <PlaceholderTab title="Звук" />}
            {tab === 'video' && <PlaceholderTab title="Видео" />}
            {tab === 'controls' && <PlaceholderTab title="Управление" />}
          </div>
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button type="button" onClick={close} style={primaryBtnStyle}>Готово</button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Interface tab — fully wired
 * ──────────────────────────────────────────────────────────────── */

function InterfaceTab() {
  const settings = useUiSettings();
  const set = useUiSettings((s) => s.set);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Section title="Действия">
        <Checkbox
          checked={settings.showRightBar1}
          onChange={(v) => set('showRightBar1', v)}
          label="Дополнительная панель умений (правая)"
        />
        <Checkbox
          checked={settings.showRightBar2}
          onChange={(v) => set('showRightBar2', v)}
          label="Дополнительная панель умений (правая внутренняя)"
        />
        <Checkbox
          checked={settings.showBottomLeft}
          onChange={(v) => set('showBottomLeft', v)}
          label="Дополнительная панель умений (нижняя левая)"
        />
        <Checkbox
          checked={settings.showBottomRight}
          onChange={(v) => set('showBottomRight', v)}
          label="Дополнительная панель умений (нижняя правая)"
        />
      </Section>

      <Section title="Информация">
        <Checkbox
          checked={settings.showCastBar}
          onChange={(v) => set('showCastBar', v)}
          label="Полоса заклинания"
        />
        <Checkbox
          checked={settings.showXpBar}
          onChange={(v) => set('showXpBar', v)}
          label="Шкала опыта"
        />
        <Checkbox
          checked={settings.showRepBar}
          onChange={(v) => set('showRepBar', v)}
          label="Шкала репутации"
        />
        <Checkbox
          checked={settings.showLatencyMeter}
          onChange={(v) => set('showLatencyMeter', v)}
          label="Метр пинга"
        />
        <Checkbox
          checked={settings.showFps}
          onChange={(v) => set('showFps', v)}
          label="FPS"
        />
      </Section>

      <Section title="Мобильный интерфейс">
        <Checkbox
          checked={settings.forceMobileUi}
          onChange={(v) => set('forceMobileUi', v)}
          label="Принудительный мобильный интерфейс"
        />
      </Section>

      <Section title="Размеры">
        <Slider
          label="Размер интерфейса"
          min={0.75}
          max={1.5}
          step={0.05}
          value={settings.uiScale}
          onChange={(v) => set('uiScale', v)}
          format={(v) => `${Math.round(v * 100)}%`}
        />
        <Slider
          label="Размер шрифта чата"
          min={11}
          max={18}
          step={1}
          value={settings.chatFontSize}
          onChange={(v) => set('chatFontSize', v)}
          format={(v) => `${v}px`}
        />
        <Slider
          label="Размер боевого текста"
          min={14}
          max={32}
          step={1}
          value={settings.combatTextSize}
          onChange={(v) => set('combatTextSize', v)}
          format={(v) => `${v}px`}
        />
      </Section>
    </div>
  );
}

function PlaceholderTab({ title }: { title: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Section title={title}>
        <div style={{ color: 'var(--aiwari-fg-muted, #B19B7E)', fontStyle: 'italic', padding: '12px 0' }}>
          Настройки скоро появятся.
        </div>
      </Section>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Building blocks
 * ──────────────────────────────────────────────────────────────── */

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
          fontSize: 13,
          letterSpacing: 0.5,
          color: 'var(--aiwari-gold-soft, #FFD479)',
          marginBottom: 6,
          paddingBottom: 4,
          borderBottom: '1px solid var(--aiwari-frame-edge, #5C4520)',
        }}
      >
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{children}</div>
    </div>
  );
}

function Checkbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        fontSize: 14,
        color: 'var(--aiwari-fg, #E8E1DA)',
        fontFamily: 'var(--aiwari-font-body, "EB Garamond", Georgia, serif)',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: 'var(--aiwari-gold, #C9A96A)' }}
      />
      <span>{label}</span>
    </label>
  );
}

function Slider({
  label,
  min,
  max,
  step,
  value,
  onChange,
  format,
}: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
  format: (v: number) => string;
}) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontSize: 13,
        color: 'var(--aiwari-fg, #E8E1DA)',
        fontFamily: 'var(--aiwari-font-body, "EB Garamond", Georgia, serif)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>{label}</span>
        <span
          style={{
            color: 'var(--aiwari-gold-soft, #FFD479)',
            fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
            fontSize: 12,
          }}
        >
          {format(value)}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ accentColor: 'var(--aiwari-gold, #C9A96A)', width: '100%' }}
      />
    </label>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Static styles
 * ──────────────────────────────────────────────────────────────── */

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
};

const panelStyle: CSSProperties = {
  width: 600,
  height: 500,
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--aiwari-bg-card-solid, #161210)',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
  borderRadius: 'var(--aiwari-frame-radius, 4px)',
  boxShadow: '0 8px 28px rgba(0,0,0,0.85), 0 0 24px rgba(201,169,106,0.18)',
  color: 'var(--aiwari-fg, #E8E1DA)',
  fontFamily: 'var(--aiwari-font-body, "EB Garamond", Georgia, serif)',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 14px',
  borderBottom: '1px solid var(--aiwari-frame-edge, #5C4520)',
  background: 'linear-gradient(180deg, #1F1813, #161210)',
};

const titleStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 17,
  letterSpacing: 1,
  color: 'var(--aiwari-gold-soft, #FFD479)',
};

const closeBtnStyle: CSSProperties = {
  width: 28,
  height: 28,
  background: 'transparent',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
  color: 'var(--aiwari-fg-muted, #B19B7E)',
  fontSize: 18,
  lineHeight: 1,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const bodyStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  minHeight: 0,
};

const tabRailStyle: CSSProperties = {
  width: 160,
  display: 'flex',
  flexDirection: 'column',
  padding: '8px 0',
  borderRight: '1px solid var(--aiwari-frame-edge, #5C4520)',
  background: 'rgba(11,9,7,0.5)',
};

const tabBtnStyle: CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  background: 'transparent',
  border: 'none',
  borderLeft: '3px solid transparent',
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 13,
  letterSpacing: 0.4,
  cursor: 'pointer',
};

const contentStyle: CSSProperties = {
  flex: 1,
  padding: '14px 18px',
  overflowY: 'auto',
};

const footerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '10px 14px',
  borderTop: '1px solid var(--aiwari-frame-edge, #5C4520)',
  background: 'linear-gradient(180deg, #161210, #0B0907)',
};

const primaryBtnStyle: CSSProperties = {
  padding: '6px 18px',
  background: 'linear-gradient(180deg, #2A2014, #1F1813)',
  border: '1px solid var(--aiwari-gold-dark, #7A5A18)',
  color: 'var(--aiwari-gold-soft, #FFD479)',
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 13,
  letterSpacing: 0.5,
  cursor: 'pointer',
};

export default SettingsPanel;
